import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import { epocusTheme } from "../../../_lib/epocus-theme-provider";
import type { EpocusProgramUsersQuery } from "./__generated__/program-users-operations.gql";
import { roleLabelForRole } from "./program-user-roles";
import { ProgramUserRowMenu } from "./program-user-row-menu";

type ProgramUser = EpocusProgramUsersQuery["listProgramUsers"][number];

type Props = {
  users: ReadonlyArray<ProgramUser>;
};

const fullName = (user: ProgramUser): string =>
  [user.user.firstName, user.user.lastName].filter(Boolean).join(" ") || "—";

const CardStack = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 12,
  [epocusTheme.breakpoints.up("sm")]: {
    display: "none",
  },
});

const UserCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  padding: 16,
  border: `1px solid ${EPOCUS_BORDER}`,
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
});

const CardBody = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
});

const CardName = styled(Typography)({
  fontSize: rem(15),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const CardDetail = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const TableWrap = styled(Box)({
  display: "none",
  [epocusTheme.breakpoints.up("sm")]: {
    display: "block",
  },
});

export const ProgramUsersList: FC<Props> = ({ users }) => {
  return (
    <>
      <CardStack>
        {users.map((member) => (
          <UserCard key={member.epocusUserId}>
            <CardBody>
              <CardName>{fullName(member)}</CardName>
              <CardDetail>{roleLabelForRole(member.role)}</CardDetail>
              <CardDetail>{member.user.telephone || "—"}</CardDetail>
              <CardDetail>{member.user.email}</CardDetail>
            </CardBody>
            <ProgramUserRowMenu
              epocusUserId={member.epocusUserId}
              status={member.user.status}
            />
          </UserCard>
        ))}
      </CardStack>

      <TableWrap>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>First name</TableCell>
              <TableCell>Last name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Mobile number</TableCell>
              <TableCell>Email</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((member) => (
              <TableRow key={member.epocusUserId}>
                <TableCell>{member.user.firstName || "—"}</TableCell>
                <TableCell>{member.user.lastName || "—"}</TableCell>
                <TableCell>{roleLabelForRole(member.role)}</TableCell>
                <TableCell>{member.user.telephone || "—"}</TableCell>
                <TableCell>{member.user.email}</TableCell>
                <TableCell align="right">
                  <ProgramUserRowMenu
                    epocusUserId={member.epocusUserId}
                    status={member.user.status}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableWrap>
    </>
  );
};
